import React, { useEffect, useRef, useState } from 'react';
import { Sidebar } from '../src/NewDashBoard/Sidebar';
import { Navbar } from '../src/NewDashBoard/Navbar';
import { BottomNav } from '../src/NewDashBoard/BottomNav';
import { Dashboard } from '../src/NewDashBoard/Dashboard';
import { Finance } from '../src/NewDashBoard/Finance';
import { HandLoans } from '../src/NewDashBoard/HandLoans';
import  {DayReport}  from '../src/NewDashBoard/DayReport'
import { Consultancy } from '../src/NewDashBoard/Consultancy';
import { Settings } from '../src/NewDashBoard/Settings';
import { MasterDashboard } from '../src/NewDashBoard/MasterDashboard';
import { Expenses } from '../src/NewDashBoard/Expenses';
import { Employees } from '../src/NewDashBoard/Employees';
import { AuditLogs } from '../src/NewDashBoard/AuditLogs';
import { ErpSuite } from '../src/NewDashBoard/ErpSuite';
import { Accounts } from '../src/NewDashBoard/Accounts';
import { Menu, Lock, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  getOrganizationById,
  getTenantId,
  isCurrentTenantOnHold,
  needsCheckoutResume,
  touchOrganizationLastActivity,
  clearAuthAndTenant,
  cacheOrganizationFromApi,
} from './lib/tenant';
import { hydrateTenantWorkspace } from './lib/cloudSync';
import { applyAuthLedgers } from './lib/ledger';
import { authApi, ledgerApi } from './api';
import { hasAuthToken, SESSION_EXPIRED_EVENT, ApiError } from './api/client';
import { UserList } from './NewDashBoard/dashboard/UserList';
import { OverdueManagement } from './NewDashBoard/OverdueManagement';
import { canAccessNav, canWriteModule, firstAllowedNav, isLedgerDeskUser, setCurrentUser, NAV_MODULE } from './lib/permissions';
import { AppToastContainer } from './components/common/AppToastContainer';
import { isExternalBillingDisabled } from './lib/platform';

const NAV_ORDER = ['master', 'dashboard', 'customers', 'finance', 'loans', 'accounts', 'report', 'erp', 'audit', 'consultancy', 'expenses', 'employees', 'settings'];

function App() {
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState(() => firstAllowedNav(NAV_ORDER));
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDisclaimer] = useState(false);
  const [customerFocusId, setCustomerFocusId] = useState<string | null>(null);
  const [settingsInitialSection, setSettingsInitialSection] = useState<string | null>(null);
  const mainRef = useRef<HTMLElement>(null);

  const openSettingsSection = (section: string) => {
    setSettingsInitialSection(section);
    setActiveItem('settings');
  };

  /** Open the Customers view focused on a specific borrower (from notifications). */
  const openCustomer = (id: string) => {
    setCustomerFocusId(id);
    setActiveItem('customers');
  };

  // Always start a freshly-opened page at the top instead of inheriting the
  // previous page's scroll position.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0 });
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, left: 0 });
  }, [activeItem]);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    const token = localStorage.getItem('token');
    if (!isLoggedIn || !token) {
      navigate('/login');
      return;
    }
    if (localStorage.getItem('role') === 'SUPER_ADMIN' && token.startsWith('super-frontend-token')) {
      navigate('/super-admin', { replace: true });
      return;
    }
    if (!hasAuthToken()) {
      clearAuthAndTenant();
      navigate('/login', { replace: true });
      return;
    }
    if (isCurrentTenantOnHold()) {
      navigate('/account-held', { replace: true });
      return;
    }
    if (needsCheckoutResume()) {
      if (isExternalBillingDisabled()) {
        clearAuthAndTenant();
        navigate('/login', { replace: true });
        return;
      }
      const tid = getTenantId();
      navigate(tid ? `/checkout?tenant=${encodeURIComponent(tid)}` : '/checkout');
      return;
    }
    const tenantId = getTenantId();
    if (tenantId && getOrganizationById(tenantId)) {
      const touchKey = `ef_org_activity_${tenantId}`;
      if (!sessionStorage.getItem(touchKey)) {
        touchOrganizationLastActivity(tenantId);
        sessionStorage.setItem(touchKey, '1');
      }
    }
    // Pull the latest workspace data from MongoDB into the local cache.
    hydrateTenantWorkspace();
    ledgerApi.ensureDefault().catch(() => {});

    authApi
      .me()
      .then((res) => {
        if (res.organization) cacheOrganizationFromApi(res.organization);
        setCurrentUser(res.user);
        applyAuthLedgers(res);
        if (isCurrentTenantOnHold()) {
          navigate('/account-held', { replace: true });
          return;
        }
        if (needsCheckoutResume()) {
          if (isExternalBillingDisabled()) {
            clearAuthAndTenant();
            navigate('/login', { replace: true });
            return;
          }
          const tid = getTenantId();
          navigate(tid ? `/checkout?tenant=${encodeURIComponent(tid)}` : '/checkout');
          return;
        }
        setActiveItem((curr) => (canAccessNav(curr) ? curr : firstAllowedNav(NAV_ORDER)));
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          clearAuthAndTenant();
          navigate('/login', { replace: true });
        }
      });
  }, [navigate]);

  useEffect(() => {
    const onSessionExpired = () => {
      clearAuthAndTenant();
      navigate('/login', { replace: true });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, [navigate]);

  /** If another tab applies a payment hold to this tenant, kick out without requiring a full reload. */
  useEffect(() => {
    const recheckHold = () => {
      if (localStorage.getItem('isLoggedIn') !== 'true') return;
      const token = localStorage.getItem('token');
      if (!token || token.startsWith('super-frontend-token')) return;
      if (localStorage.getItem('role') === 'SUPER_ADMIN') return;
      if (isCurrentTenantOnHold()) {
        navigate('/account-held', { replace: true });
      }
    };
    window.addEventListener('focus', recheckHold);
    window.addEventListener('storage', recheckHold);
    return () => {
      window.removeEventListener('focus', recheckHold);
      window.removeEventListener('storage', recheckHold);
    };
  }, [navigate]);

  const renderContent = () => {
    if (!canAccessNav(activeItem)) {
      return (
        <div className="flex min-h-[50vh] flex-col items-center justify-center rounded-2xl border border-zinc-200 bg-white p-8 text-center shadow-sm">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-600">
            <Lock className="h-6 w-6" />
          </div>
          <h2 className="text-lg font-semibold text-zinc-900">No access to this section</h2>
          <p className="mt-1 max-w-sm text-sm text-zinc-500">
            Your administrator hasn’t granted you access to this module. Contact a workspace owner if you need it.
          </p>
        </div>
      );
    }
    // Read-only notice: the user can view this module but lacks write access.
    // Individual modules also disable their create/edit/delete controls.
    const moduleId = NAV_MODULE[activeItem];
    const readOnly =
      !!moduleId && moduleId !== 'dashboard' && !isLedgerDeskUser() && !canWriteModule(activeItem);
    const readOnlyBanner = readOnly ? (
      <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
        <Eye className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          You have <strong>read-only</strong> access to this section. Viewing is allowed, but creating,
          editing, and deleting are disabled.
        </span>
      </div>
    ) : null;

    const page = (() => {
    switch (activeItem) {
      case 'master':
        return (
          <MasterDashboard
            setActiveItem={setActiveItem}
            onOpenSettings={openSettingsSection}
          />
        );
      case 'dashboard':
        return <Dashboard setActiveItem={setActiveItem}/>;
      case 'expenses':
        return <Expenses setActiveItem={setActiveItem}/>;
      case 'employees':
        return <Employees setActiveItem={setActiveItem}/>;
      case 'finance':
        return <Finance setActiveItem={setActiveItem}/>;
      case 'loans':
        return <HandLoans setActiveItem={setActiveItem}/>;
      case 'accounts':
        return <Accounts setActiveItem={setActiveItem}/>;
      case 'report':
        return <DayReport setActiveItem={setActiveItem}/>;
      case 'audit':
        return <AuditLogs setActiveItem={setActiveItem}/>;
      case 'erp':
        return <ErpSuite setActiveItem={setActiveItem}/>;
      case 'consultancy':
        return <Consultancy setActiveItem={setActiveItem}/>;
      case 'settings':
        return (
          <Settings
            setActiveItem={setActiveItem}
            initialSection={settingsInitialSection}
            onConsumeInitialSection={() => setSettingsInitialSection(null)}
          />
        );
      case 'customers':
        return (
          <UserList
            onClose={() => setActiveItem('dashboard')}
            setActiveItem={setActiveItem}
            focusUserId={customerFocusId}
            onConsumeFocus={() => setCustomerFocusId(null)}
          />
        );
      case 'overdue':
        return (
          <OverdueManagement
            setActiveItem={setActiveItem}
            onOpenCustomer={openCustomer}
          />
        );
      default:
        return <Dashboard setActiveItem={setActiveItem}/>;
    }
    })();

    return (
      <>
        {readOnlyBanner}
        {page}
      </>
    );
  };

  const handlePricingClick = () => {
    if (isExternalBillingDisabled()) return;
    navigate('/pricing');
  };

  


  return (
  <>
  
     <div className="app-shell flex w-full max-w-full overflow-x-hidden bg-zinc-100 font-sans antialiased">
      {/* Mobile Sidebar Overlay (above the bottom nav so the drawer never merges with it) */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[55] lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar (solid bg + shadow so the mobile drawer is never see-through) */}
      <div className={`
        fixed inset-y-0 left-0 z-[60] w-64 transform bg-black pt-[var(--safe-area-inset-top,env(safe-area-inset-top,0px))] pb-[var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))] shadow-2xl transition-transform duration-200 ease-in-out lg:relative lg:z-30 lg:translate-x-0 lg:bg-zinc-950 lg:pt-0 lg:pb-0 lg:shadow-none
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <Sidebar 
          activeItem={activeItem} 
          setActiveItem={(item) => {
            setActiveItem(item);
            setIsSidebarOpen(false);
          }} 
        />
      </div>

      {/* Main Content */}
      <div className="flex min-w-0 max-w-full flex-1 flex-col">
        <Navbar activeItem={activeItem} setActiveItem={setActiveItem} onOpenCustomer={openCustomer}>
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="rounded-md p-2 text-zinc-600 hover:bg-zinc-100 lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
        </Navbar>
        <main
          ref={mainRef}
          className={`app-scroll flex-1 overflow-auto px-3 pb-[calc(4.75rem+var(--safe-area-inset-bottom,env(safe-area-inset-bottom,0px))+var(--keyboard-height,0px))] sm:px-4 lg:px-6 lg:pb-8 ${
            activeItem === 'settings' ? 'pt-0' : 'pt-3 sm:pt-4 lg:pt-6'
          }`}
        >
          <div className="mx-auto w-full min-w-0 max-w-7xl">
            {renderContent()}
            
          </div>

        </main>
      </div>

      {/* Shared mobile bottom navigation (common across all pages) */}
      <BottomNav activeItem={activeItem} setActiveItem={setActiveItem} />

      <AppToastContainer />

      {/* Add Disclaimer */}
      {showDisclaimer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="max-w-md rounded-xl border border-zinc-200 bg-white p-6 shadow-xl">
            <h2 className="mb-2 text-lg font-semibold text-zinc-900">Subscription required</h2>
            <p className="mb-6 text-sm leading-relaxed text-zinc-600">
              Your subscription has expired. Renew to continue using the application.
            </p>
            <button
              onClick={handlePricingClick}
              className="app-btn-primary w-full"
            >
              View plans
            </button>
          </div>
        </div>
      )}
    </div>
      
  </>
  );
}

export default App;